/**
 * The Meta Pixel — loaded only after the visitor accepts marketing cookies,
 * and only ever from here.
 *
 * Meta's snippet is an inline <script>, which the site's CSP (`script-src
 * 'self'`, deploy/nginx-security-headers.conf) refuses. So the same bootstrap
 * runs as part of our own bundle instead, and the policy allows exactly one
 * outside script origin, connect.facebook.net, plus www.facebook.com for the
 * requests the pixel sends. Its `<noscript>` image is left out on purpose: it
 * reports a visit before anyone could have been asked.
 *
 * This is the one remote script the app injects (see Rule 0.1 in
 * FRONTEND-PATTERNS.md), so three things keep it in its lane:
 *
 * - **It reports what we tell it and nothing else.** `autoConfig` off stops it
 *   from collecting button clicks and page metadata by itself, and
 *   `disablePushState` stops it from reporting every in-app navigation. Every
 *   PageView is sent by CookieConsent.jsx, which asks `isTrackedLocation`
 *   first; the one other event, CompleteRegistration for a new business
 *   account, goes through `trackEvent`, which asks the same and more.
 *   Automatic advanced matching, which would read the e-mail typed into a
 *   form, is a switch in Meta's Events Manager and must stay off there too.
 * - **It sees only our own public pages.** A business's booking page, the
 *   dashboard and every emailed link are not ours to report: on a booking page
 *   we are the business's processor (Terms, section 8), and an emailed link
 *   carries a credential in its URL — /booking/:token is a customer's whole
 *   authorisation, and the pixel sends Meta the full address.
 * - **Production only.** VITE_META_PIXEL_ID is set in .env.production only, so
 *   the dev server and the e2e run load nothing from Meta (they still show the
 *   banner, CookieConsent.jsx, so it can be seen locally). UAT builds from the
 *   same file, and the server also answers on its Tailscale address, so the ID
 *   alone is not enough: `isPixelHost` admits only production's own names.
 */

import { readConsent } from "./cookieConsent";

const SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

/** First-party cookies the pixel writes on our domain. */
export const META_COOKIES = ["_fbp", "_fbc"];

/**
 * EasyPlanner's own public pages: what the sitemap publishes
 * (`PublicSitemap.StaticPages` in the API) plus the Cookie Policy. A business's
 * booking page is in the sitemap too but not here — see above.
 */
export const TRACKED_PAGES = [
  "/",
  "/discover",
  "/signup",
  "/login",
  "/terms",
  "/privacy",
  "/cookies",
  "/delivery",
  "/cancellation",
  "/withdrawal",
];

/** Query parameters that carry a credential (`/signup?invite=`): a URL holding one is never reported. */
const SECRET_PARAMS = ["invite", "token", "code"];

export const metaPixelId = () => import.meta.env.VITE_META_PIXEL_ID || "";

/**
 * Production's addresses — the only ones the pixel loads on, and so the only
 * ones a production build asks about cookies on. Everything else serving the
 * same build (uat.easyplanner.ro, the Tailscale address) stays out of the ad
 * account's data.
 */
export const PIXEL_HOSTS = ["easyplanner.ro", "www.easyplanner.ro"];

// No window is the build's prerender (scripts/prerender.mjs): no address, so no
// banner in the HTML it writes.
export const isPixelHost = (
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
) => PIXEL_HOSTS.includes(hostname);

/** Whether this address may be reported to Meta, consent aside. */
export const isTrackedLocation = ({ pathname = "", search = "" }) => {
  // React Router matches paths case-insensitively and ignores a trailing
  // slash, so "/Signup/" renders the signup page and must be judged like it.
  const path = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
  if (!TRACKED_PAGES.includes(path)) return false;
  const params = new URLSearchParams(search);
  return !SECRET_PARAMS.some((name) => params.has(name));
};

let loadedId = null;
let revoked = false;

/** Meta's own bootstrap, minus the inline <script> it normally comes in. */
const installStub = () => {
  if (window.fbq) return;
  const fbq = function () {
    // `arguments`, not a rest parameter: fbevents.js replays the queue as it
    // finds it, and this is the shape Meta's snippet puts there.
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
    } else {
      fbq.queue.push(arguments);
    }
  };
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
};

/**
 * Loads the pixel, or — when it already loaded and the visitor later refused
 * and accepted again — lets it send again. Returns whether a pixel is running.
 */
export const enableMetaPixel = (id = metaPixelId()) => {
  if (!id || !isPixelHost()) return false;

  if (loadedId) {
    if (revoked) {
      window.fbq("consent", "grant");
      revoked = false;
    }
    return true;
  }

  installStub();
  // All before init, which is when the pixel reads them. With pushState
  // tracking off, Meta's script keeps the first PageView of a document and
  // drops every later one as a duplicate — in a single-page app that is every
  // page after the first. CookieConsent sends one per path, so the pixel is
  // told to accept them.
  window.fbq.disablePushState = true;
  window.fbq.allowDuplicatePageViews = true;
  window.fbq("set", "autoConfig", false, id);
  window.fbq("init", id);

  const script = document.createElement("script");
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);

  loadedId = id;
  return true;
};

/** Reports the current page. Does nothing until `enableMetaPixel` has run. */
export const trackPageView = () => {
  if (loadedId) window.fbq("track", "PageView");
};

/**
 * The parameters an event may carry to Meta: labels we write ourselves, never
 * anything a person typed. Everything else is dropped before it reaches the
 * pixel — above all the advanced-matching keys (`em`, `fn`, `ln`, `ph`, …),
 * which would hand Meta an e-mail address or a name, and `search_string`.
 */
const EVENT_PARAMS = ["content_name", "content_category", "status", "currency", "value"];

const PLAIN_VALUE = ["string", "number", "boolean"];

/**
 * Sends one standard event — a conversion such as CompleteRegistration — and
 * returns whether it did. It never loads the pixel: loading is CookieConsent's
 * call alone. So it sends only when the pixel is already running here, the
 * visitor's marketing consent still stands at this moment, and the address is
 * one a PageView could be sent from (`isTrackedLocation`) — the pixel attaches
 * the full URL to every event, so /signup?invite=… sends nothing.
 *
 * A tracking call must never break what it measures, so it does not throw.
 */
export const trackEvent = (name, params = {}) => {
  if (!loadedId || revoked) return false;
  if (readConsent()?.marketing !== true) return false;
  if (!isTrackedLocation(window.location)) return false;

  const allowed = Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) => EVENT_PARAMS.includes(key) && PLAIN_VALUE.includes(typeof value),
    ),
  );
  try {
    window.fbq("track", name, allowed);
    return true;
  } catch {
    return false;
  }
};

/**
 * Removes the pixel's cookies from our domain. They are written on the widest
 * domain allowed (".easyplanner.ro"), and a cookie is removed only by naming
 * the domain it was set on, so every suffix of the host is tried, plus the
 * host-only form. The ones Meta keeps on facebook.com are out of our reach;
 * the Cookie Policy says so.
 */
export const clearMetaCookies = () => {
  const labels = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < labels.length - 1; i += 1) {
    domains.push(`; domain=.${labels.slice(i).join(".")}`);
  }
  META_COOKIES.forEach((name) =>
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }),
  );
};

/**
 * Refusal or withdrawal: the pixel stops sending (a script cannot be unloaded,
 * but Meta's consent switch silences it) and its cookies go. Safe to call when
 * it never loaded — the cookies may still be there from an earlier visit.
 */
export const disableMetaPixel = () => {
  if (loadedId && !revoked) {
    window.fbq("consent", "revoke");
    revoked = true;
  }
  clearMetaCookies();
};
